import type { BotCapabilities, BotConfig, BotFollowUps, FollowUpStep } from '../../types/leadBot.js';
import { FOLLOW_UP_TOKENS } from '../fitnessBot/followUps.js';
import { normalizeAllowlistEntry } from '../leadBot/allowlist.js';

// WHAT A CLUB MAY CHANGE ABOUT ITS OWN ASSISTANT IN THE PORTAL (club onboarding, Phase 1 C). PURE.
//
// ⚠️⚠️ The editable set is exactly: assistant name, the four capability toggles, follow-up wording / timing / on-off, the handover
// contact, and the test numbers. NOT here, ever: the Kyck instructions, the caps, the number binding, the vertical and the playbook,
// the reply mode, the follow-up SEND WINDOW and the opt-out rules. The server's strict schema has no key for any of them.

export const PORTAL_BOT_BOUNDS = {
  assistantNameMax: 20,
  testNumbersMax: 10,
  /** 1 hour to 14 days, in minutes. */
  followUpDelayMinutes: { min: 60, max: 14 * 24 * 60 },
  followUpMessageMax: 480,
} as const;

/** 1 to 20 characters, letters and spaces. It appears in the first message, so it must read as a name. */
export function validateAssistantName(raw: string): string | null {
  const name = raw.trim();
  if (name.length < 1 || name.length > PORTAL_BOT_BOUNDS.assistantNameMax) return `The assistant’s name needs 1 to ${PORTAL_BOT_BOUNDS.assistantNameMax} characters.`;
  if (!/^[\p{L}][\p{L} ]*$/u.test(name)) return 'The assistant’s name can use letters and spaces only.';
  return null;
}

/** ⚠️ "Book tours" can be turned ON only when the bot has a booking calendar. Turning it off is always allowed. */
export function validateCapabilities(next: BotCapabilities, hasBookingCalendar: boolean): string | null {
  if (next.bookAppointments && !hasBookingCalendar) return 'Book tours can only be turned on once Kyck has set up a booking calendar for your club.';
  return null;
}

/** A follow-up wording may use only the known tokens, each exactly as {{Name}}; anything else would publish literally or never send. */
export function validateFollowUpStep(step: FollowUpStep, label: string): string | null {
  const msg = step.message;
  if (typeof msg !== 'string' || msg.trim().length === 0) return `${label}: write the message.`;
  if (msg.length > PORTAL_BOT_BOUNDS.followUpMessageMax) return `${label}: keep it under ${PORTAL_BOT_BOUNDS.followUpMessageMax} characters.`;
  const known = new Set<string>(FOLLOW_UP_TOKENS);
  for (const m of msg.matchAll(/\{\{\s*([^{}]*?)\s*\}\}/g)) {
    if (!known.has(m[1] ?? '')) return `${label}: {{${m[1]}}} is not a token we know. You can use ${FOLLOW_UP_TOKENS.map((t) => `{{${t}}}`).join(' ')}.`;
  }
  if (/\{\{|\}\}/.test(msg.replace(/\{\{\s*[^{}]*?\s*\}\}/g, ''))) return `${label}: a { or } is left open.`;
  const d = step.delayMinutes;
  const { min, max } = PORTAL_BOT_BOUNDS.followUpDelayMinutes;
  if (!Number.isInteger(d) || d < min || d > max) return `${label}: the wait must be between 1 hour and 14 days.`;
  return null;
}

export interface PortalFollowUpsInput {
  noResponse: FollowUpStep[];
  incomplete: FollowUpStep[];
}

/**
 * Applies the club's follow-up edits to the STORED sequence: same number of steps (no add or remove), the send window and everything
 * else the stored value carries are kept. Returns an error sentence or the merged value.
 */
export function mergePortalFollowUps(stored: BotFollowUps | undefined, input: PortalFollowUpsInput): { error: string } | { value: BotFollowUps } {
  if (!stored) return { error: 'This assistant has no follow-ups set up yet. Talk to Kyck.' };
  if (input.noResponse.length !== stored.noResponse.length || input.incomplete.length !== stored.incomplete.length) {
    return { error: 'You can change a follow-up’s wording, wait and on/off, but not add or remove steps.' };
  }
  for (const [i, s] of input.noResponse.entries()) {
    const e = validateFollowUpStep(s, `No reply, step ${i + 1}`);
    if (e) return { error: e };
  }
  for (const [i, s] of input.incomplete.entries()) {
    const e = validateFollowUpStep(s, `Went quiet, step ${i + 1}`);
    if (e) return { error: e };
  }
  const clean = (s: FollowUpStep): FollowUpStep => ({ enabled: s.enabled, delayMinutes: s.delayMinutes, message: s.message.trim() });
  return { value: { ...stored, noResponse: input.noResponse.map(clean), incomplete: input.incomplete.map(clean) } };
}

/** At most 10 test numbers, each readable as a real number. Returns the normalised list or an error. */
export function validateTestNumbers(raw: readonly string[], country: 'AU' | 'NZ' | 'GB' | 'US' | string): { error: string } | { numbers: string[] } {
  const numbers: string[] = [];
  const bad: string[] = [];
  for (const r of raw) {
    const n = normalizeAllowlistEntry(r, country as never);
    if (!n) bad.push(r);
    else if (!numbers.includes(n)) numbers.push(n);
  }
  if (bad.length > 0) return { error: 'These are not numbers we can text. Use the full mobile number, or the +61 form.' };
  if (numbers.length > PORTAL_BOT_BOUNDS.testNumbersMax) return { error: `You can add up to ${PORTAL_BOT_BOUNDS.testNumbersMax} test numbers.` };
  return { numbers };
}

/** Handover contact: name up to 80, an E.164-readable phone and a plausible email; each may be blank (cleared). */
export function validateHandoffContact(input: { name: string; phone: string; email: string }, country: string): { error: string } | { name: string | null; phone: string | null; email: string | null } {
  const name = input.name.trim();
  if (name.length > 80) return { error: 'The contact name is too long.' };
  const phoneRaw = input.phone.trim();
  let phone: string | null = null;
  if (phoneRaw !== '') {
    phone = normalizeAllowlistEntry(phoneRaw, country as never);
    if (!phone) return { error: 'That contact number is not one we can text. Use the full mobile number, or the +61 form.' };
  }
  const email = input.email.trim();
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { error: 'That email address does not look right.' };
  return { name: name || null, phone, email: email || null };
}

/** The bot fields a portal save may write (a subset of BotConfig): used to keep the patch honest. */
export type PortalBotPatch = Partial<Pick<BotConfig, 'assistantName' | 'capabilities' | 'followUps' | 'handoffContactName' | 'handoffPhone' | 'handoffEmail' | 'testPhoneAllowlist'>>;
